import LayoutEditorImageModal from '@components/layout/modal/LayoutEditorImageModal';
import { DynamicForm, seekFormFieldNumberToString } from '@hclinear/hc-linear-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from '@redux/store/store';
import React, { useMemo, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { editElementModalFormFields } from '@/components';
import { ElementDTOUnion, ElementTypeDTO, FontSelectDTO } from '@/model';
import { layoutEditorActions } from '@/redux';
import { IconSelectOption } from '@/type';
import { buildValidationSchema, sortArrayAsc } from '@/util';

interface EditElementFormProps {
  onSubmit: (result: Partial<ElementDTOUnion>) => void;
  element: ElementDTOUnion;
  fetchedRasterFonts: FontSelectDTO[];
}

export const EditElementForm = ({
  onSubmit,
  fetchedRasterFonts,
}: EditElementFormProps) => {
  const dispatch = useDispatch();
  const { elements, selectedLayoutId, fetchedLayout } = useSelector(
    (state) => state.layoutEditor,
  );

  const [imageModalConfig, setImageModalConfig] = useState<ImageModalConfig>({
    open: false,
    elementType: null,
  });

  const element = elements.find((el) => el.id === selectedLayoutId);

  const onFieldBlur = async () => {
    const fieldNames = Object.keys(defaultValues) as (keyof ElementDTOUnion)[];
    const isValid = await formMethods.trigger(fieldNames);
    if (isValid) {
      const currentValues = formMethods.getValues();
      dispatch(
        layoutEditorActions.updateElementProperty({ id: element.id, ...currentValues }),
      );
    }
  };

  const formFieldsForValidation = editElementModalFormFields(
    fetchedRasterFonts,
    fetchedLayout.displayWidth,
    fetchedLayout.displayHeight,
    Number(element?.contentWidth),
    Number(element?.contentHeight),
    element,
    element?.elementType,
    onFieldBlur,
  );

  // @ts-ignore
  const defaultValues = seekFormFieldNumberToString(element, formFieldsForValidation);

  const formMethods = useForm<ElementDTOUnion>({
    defaultValues,
    resolver: yupResolver(
      // @ts-ignore
      buildValidationSchema(formFieldsForValidation) as Yup.ObjectSchema<ElementDTOUnion>,
    ) as Resolver<ElementDTOUnion>,
  });

  const onFontNameChange = (selectedValue: string) => {
    const matchedFont = fetchedRasterFonts?.find(
      (rasterFont) => rasterFont.ttFontName === selectedValue,
    );

    const iconSelectOptions: IconSelectOption[] =
      sortArrayAsc(matchedFont?.rasterFonts, 'size')?.map((rasterFont) => ({
        value: rasterFont.id,
        label: rasterFont.size,
      })) || [];
    return iconSelectOptions;
  };

  const { watch } = formMethods;
  const fontNameWatch = watch('ttFontName');

  const rasterFontSelectOptions: IconSelectOption[] = useMemo(() => {
    const matchedFont = fetchedRasterFonts?.find(
      (rasterFont) => rasterFont.ttFontName === fontNameWatch,
    );

    return (
      sortArrayAsc(matchedFont?.rasterFonts, 'size')?.map((rasterFont) => ({
        value: rasterFont.id,
        label: rasterFont.size,
      })) || []
    );
  }, [fontNameWatch]);

  const onImageModalOpen = (elementType: ElementTypeDTO) => {
    setImageModalConfig({ open: true, elementType });
  };

  const onImageModalClose = (_: boolean) => {
    setImageModalConfig({ open: false, elementType: null });
  };

  const formFields = editElementModalFormFields(
    fetchedRasterFonts,
    fetchedLayout.displayWidth,
    fetchedLayout.displayHeight,
    Number(element?.contentWidth),
    Number(element?.contentHeight),
    element,
    element?.elementType,
    onFieldBlur,
    rasterFontSelectOptions,
    onFontNameChange,
    onImageModalOpen,
  );

  return (
    <>
      <DynamicForm<Partial<ElementDTOUnion>>
        formMethods={formMethods}
        formFields={formFields}
        disableCancelButton
        hideActionButtons
        onSubmit={onSubmit}
      />
      {imageModalConfig.open && (
        <LayoutEditorImageModal
          {...imageModalConfig}
          handleClose={onImageModalClose}
          modalType={'edit'}
        />
      )}
    </>
  );
};

type ImageModalConfig = {
  open: boolean;
  elementType: ElementTypeDTO;
};
